import React, { useMemo } from 'react';
import cx from 'classnames';

import { Checkbox, Icon, Input, TextArea } from 'design-system-web';

import Dropdown from 'components/form/Dropdown';
import Radio from 'components/form/Radio';
import { S } from 'components/typography';
import { VisitorCustomFieldType } from 'graphql-query-contracts';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';


import type { CustomFieldOptionItem, CustomFieldRendererProps } from './customField.types';

import styles from './CustomFieldRenderer.scss';

function getOptionDisplay(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.displayText;
}

function getOptionValue(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.value;
}

const EMPTY_CUSTOM_FIELD_OPTIONS: CustomFieldOptionItem[] = [];

export function CustomFieldRenderer({
  field,
  disabled = true,
  value,
  onChange,
  error,
  isRequired,
  compact = false,
}: CustomFieldRendererProps) {
  const optionList = field.options ?? EMPTY_CUSTOM_FIELD_OPTIONS;
  const isInteractive = !disabled && !!onChange;
  const stringValue = typeof value === 'string' ? value : '';
  const arrayValue = Array.isArray(value) ? value : [];

  const items = useMemo(
    () =>
      optionList.map(opt => ({
        value: getOptionValue(opt),
        displayText: getOptionDisplay(opt),
      })),
    [optionList]
  );

  function renderSelectField() {
    const dropdownItems = items.map(opt => ({ value: opt.value, label: opt.displayText }));

    return (
      <div data-test={`customField-select-${field.fieldKey}`}>
        <Dropdown
          disabled={disabled}
          placeholder={field.placeholder ?? ''}
          value={stringValue !== '' ? stringValue : null}
          items={dropdownItems}
          onValueChange={isInteractive ? val => onChange(val) : () => undefined}
          doTranslation={false}
          className={error ? styles.dropdownError : undefined}
        />
      </div>
    );
  }

  function renderRadioField() {
    return (
      <div className={styles.options} data-test={`customField-radio-${field.fieldKey}`}>
        {items.map((item, index) => (
          <Radio
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            name={field.fieldKey}
            value={item.value}
            text={item.displayText}
            selected={isInteractive ? stringValue : undefined}
            disabled={disabled}
            onChange={isInteractive ? val => onChange(val) : () => undefined}
            doTranslate={false}
            className={compact ? styles.optionItem : undefined}
          />
        ))}
      </div>
    );
  }

  function renderCheckboxField() {
    function handleCheckboxChange(val: string) {
      onChange?.(
        arrayValue.includes(val)
          ? arrayValue.filter(v => v !== val)
          : [...arrayValue, val]
      );
    }

    return (
      <div className={styles.options} data-test={`customField-checkbox-${field.fieldKey}`}>
        {items.map((item, index) => (
          <Checkbox
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            name={field.fieldKey}
            value={item.value}
            text={item.displayText}
            selected={isInteractive ? arrayValue.some(v => v === item.value) : false}
            disabled={disabled}
            onChange={isInteractive ? handleCheckboxChange : undefined}
            className={compact ? styles.optionItem : undefined}
          />
        ))}
      </div>
    );
  }

  function renderTextareaField() {
    return (
      <TextArea
        value={stringValue}
        onChange={isInteractive ? val => onChange(val) : () => undefined}
        placeholder={field.placeholder ?? ''}
        disabled={disabled}
        testId={`customField-textarea-${field.fieldKey}`}
        className={error ? styles.fieldBorderError : undefined}
      />
    );
  }

  function renderTextField() {
    return (
      <Input
        fieldName={field.fieldKey}
        value={stringValue}
        onChange={isInteractive ? val => onChange(val) : () => undefined}
        placeholder={field.placeholder ?? ''}
        disabled={disabled}
        testId={`customField-text-${field.fieldKey}`}
        inputClassName={error ? styles.fieldBorderError : undefined}
      />
    );
  }

  function renderInput() {
    switch (field.fieldType) {
      case VisitorCustomFieldType.Textarea:
        return renderTextareaField();
      case VisitorCustomFieldType.SelectDd:
        return renderSelectField();
      case VisitorCustomFieldType.SelectRadio:
        return renderRadioField();
      case VisitorCustomFieldType.MultiSelect:
        return renderCheckboxField();
      default:
        return renderTextField();
    }
  }

  function renderInputWithIcon() {
    const input = renderInput();

    const supportsIconOverlay =
      field.fieldType !== VisitorCustomFieldType.SelectRadio &&
      field.fieldType !== VisitorCustomFieldType.MultiSelect &&
      field.fieldType !== VisitorCustomFieldType.SelectDd;

    const isEmpty = !value || (Array.isArray(value) ? value.length === 0 : String(value).length === 0);

    if (!error || !supportsIconOverlay || !isEmpty) {
      return input;
    }

    return (
      <div className={cx(styles.inputWithIconWrapper, styles.hasErrorIcon)}>
        {input}
        <div className={styles.errorIconOverlay}>
          <div className={styles.errorIconOverlayInner}>
            <Icon
              name="exclamation-circle"
              set={ICON_SET_FONTAWESOME}
              size="small"
              className={styles.errorIconSvg}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(styles.fieldWrapper, {
        [styles.interactive]: isInteractive,
        [styles.hasError]: !!error,
      })}
      data-test={`customFieldRenderer-${field.fieldKey}`}
    >

      <S mb={1}>
        {field.fieldName}
        {isRequired && <span className={styles.required}>*</span>}
      </S>
      {renderInputWithIcon()}
      {error && (
        <p
          className={styles.fieldError}
          data-test={`customFieldError-${field.fieldKey}`}
          role="alert"
        >
          {error}
        </p>
      )}

    </div>
  );
}

